import {
    afterNextRender,
    computed,
    Directive,
    ElementRef,
    inject,
    input,
    Renderer2,
} from '@angular/core';
import type { OnInit } from '@angular/core';
import { MatSelect } from '@angular/material/select';
import type { InputSize } from './input';

const QPX_SELECT_PANEL_CLASS = 'qpx-select-panel';

@Directive({
    // eslint-disable-next-line @angular-eslint/directive-selector
    selector: 'mat-select[qpx]',
    host: {
        '[class]': 'className()',
    },
})
export class QpxSelect implements OnInit {
    //#region Dependency injection
    private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);
    private readonly select = inject(MatSelect);
    private readonly renderer = inject(Renderer2);
    //#endregion

    //#region Inputs
    public readonly id = input.required<string>();
    public readonly ariaLabelledBy = input.required<string>({
        // eslint-disable-next-line @angular-eslint/no-input-rename
        alias: 'aria-labelledby',
    });

    public readonly size = input<InputSize>('md');
    //#endregion

    //#region State
    protected readonly className = computed(() => `qpx-select ${this.size()}`);
    //#endregion

    //#region Lifecycle hooks
    constructor() {
        afterNextRender(() => {
            const arrowEl = this.el.nativeElement.querySelector(
                '.mat-mdc-select-arrow'
            );

            if (!arrowEl) return;

            // Create and append Material Symbol icon
            const icon = this.renderer.createElement('span');
            this.renderer.addClass(icon, 'material-symbols-outlined');
            this.renderer.addClass(icon, 'custom-arrow');
            this.renderer.addClass(icon, 'qpx-icon');
            this.renderer.addClass(icon, 'md');
            this.renderer.addClass(icon, 'icon-outlined');
            this.renderer.setAttribute(icon, 'role', 'img');
            this.renderer.setAttribute(icon, 'aria-hidden', 'true');
            icon.textContent = 'keyboard_arrow_down';

            this.renderer.appendChild(arrowEl, icon);
        });
    }

    public ngOnInit(): void {
        this.select.panelClass = addPanelClass(
            this.select.panelClass,
            QPX_SELECT_PANEL_CLASS
        );
    }
    //#endregion
}

function addPanelClass(
    panelClass: MatSelect['panelClass'] | undefined,
    className: string
): MatSelect['panelClass'] {
    if (!panelClass) return className;

    if (typeof panelClass === 'string') {
        const classes = panelClass.split(/\s+/).filter(Boolean);
        return classes.includes(className) ? classes : [...classes, className];
    }

    if (Array.isArray(panelClass)) {
        return panelClass.includes(className)
            ? panelClass
            : [...panelClass, className];
    }

    if (panelClass instanceof Set) {
        return new Set([...panelClass, className]);
    }

    return { ...panelClass, [className]: true };
}
